import React, { useMemo, useState } from "react";
import useSWR from "swr";
import {
  analyzePatterns,
  buildSequenceCandidates,
  flattenHistoryEvents,
  openPatternExample,
} from "./patternResearchUtils";
import AssetLabel from "../components/common/AssetLabel";
import "./PatternResearchStyles.css";

export default function PatternResearchTab({ theme, openPopup, adminApi }) {
  const { data: historyData, error: historyError, mutate: mutateHistory, isValidating: isHistoryValidating } = useSWR("admin-pattern-history", () => adminApi.getPatternHistory(), {
    refreshInterval: 30000,
    revalidateOnFocus: false,
  });
  const { data: serverData, error: serverError, mutate: mutateServer, isValidating: isServerValidating } = useSWR("admin-pattern-research", () => adminApi.getPatternResearch(), {
    refreshInterval: 30000,
    revalidateOnFocus: false,
  });
  const [direction, setDirection] = useState("all");
  const [horizonHours, setHorizonHours] = useState(4);
  const [maxGapHours, setMaxGapHours] = useState(24);
  const [minSamples, setMinSamples] = useState(4);
  const [minWinRate, setMinWinRate] = useState(0);
  const [minAvgClose, setMinAvgClose] = useState(0);
  const [minFavPain, setMinFavPain] = useState(0);
  const [sortBy, setSortBy] = useState("avgCloseReturn");
  const [maxPatterns, setMaxPatterns] = useState(12);
  const [maxExamplesPerPattern, setMaxExamplesPerPattern] = useState(12);
  const [timeframeMode, setTimeframeMode] = useState("any");
  const [clientSideAnalysis, setClientSideAnalysis] = useState(false);
  const [results, setResults] = useState([]);
  const [analysisError, setAnalysisError] = useState("");
  const [progress, setProgress] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const flatEvents = useMemo(() => flattenHistoryEvents(historyData), [historyData]);

  const candidates = useMemo(() => {
    return buildSequenceCandidates(flatEvents, {
      direction,
      maxGapHours,
      timeframeMode,
    });
  }, [flatEvents, direction, maxGapHours, timeframeMode]);

  const serverResults = useMemo(() => {
    return Array.isArray(serverData?.results) ? serverData.results : [];
  }, [serverData]);

  const activeResults = clientSideAnalysis ? results : serverResults;

  const visibleResults = useMemo(() => {
    return activeResults
      .filter((result) => direction === "all" || result.direction === direction)
      .filter((result) => clientSideAnalysis || Number(result.horizonHours) === Number(horizonHours))
      .filter((result) => clientSideAnalysis || Number(result.maxGapHours) === Number(maxGapHours))
      .filter((result) => clientSideAnalysis || timeframeMode === "any" || result.timeframeMode === timeframeMode)
      .filter((result) => result.samples >= Number(minSamples || 1))
      .filter((result) => result.winRate * 100 >= Number(minWinRate || 0))
      .filter((result) => result.avgCloseReturn >= Number(minAvgClose || 0))
      .filter((result) => (result.expansionToPain || 0) >= Number(minFavPain || 0))
      .sort((a, b) => comparePatternResults(a, b, sortBy))
      .slice(0, Number(maxPatterns || 12));
  }, [activeResults, direction, clientSideAnalysis, horizonHours, maxGapHours, timeframeMode, minSamples, minWinRate, minAvgClose, minFavPain, sortBy, maxPatterns]);

  const runAnalysis = async () => {
    setAnalysisError("");
    setIsAnalyzing(true);
    setProgress({ completed: 0, total: 0 });
    setResults([]);
    try {
      const selected = candidates.slice(0, Number(maxPatterns || 1));
      const analyzed = await analyzePatterns(
        selected,
        { horizonHours, maxExamplesPerPattern },
        setProgress
      );
      setResults(analyzed);
    } catch (err) {
      setAnalysisError(err?.message || "Pattern analysis failed.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const progressText = progress && progress.total
    ? `${progress.completed} / ${progress.total} samples checked`
    : "";

  const refreshAll = () => {
    mutateHistory();
    mutateServer();
  };

  if (clientSideAnalysis && historyError) {
    return (
      <div className="patternRoot" data-theme={theme}>
        <ResearchTop onRefresh={refreshAll} isValidating={isHistoryValidating || isServerValidating} clientSideAnalysis={clientSideAnalysis} />
        <div className="patternError">Failed to load history.</div>
      </div>
    );
  }

  if (clientSideAnalysis && !historyData) {
    return (
      <div className="patternRoot" data-theme={theme}>
        <ResearchTop onRefresh={refreshAll} isValidating={isHistoryValidating || isServerValidating} clientSideAnalysis={clientSideAnalysis} />
        <div className="patternLoading">Loading event history...</div>
      </div>
    );
  }

  return (
    <div className="patternRoot" data-theme={theme}>
      <ResearchTop onRefresh={refreshAll} isValidating={isHistoryValidating || isServerValidating} clientSideAnalysis={clientSideAnalysis} />

      <div className="patternControls">
        <label className="patternCheck patternToggle">
          <input
            type="checkbox"
            checked={clientSideAnalysis}
            onChange={(event) => {
              setClientSideAnalysis(event.target.checked);
              setResults([]);
              setProgress(null);
            }}
          />
          Client-side analysis
        </label>

        <label className="patternField">
          <span>Direction</span>
          <select value={direction} onChange={(event) => setDirection(event.target.value)}>
            <option value="all">Both</option>
            <option value="long">Long</option>
            <option value="short">Short</option>
          </select>
        </label>

        <label className="patternField">
          <span>Forward window</span>
          <select value={horizonHours} onChange={(event) => setHorizonHours(Number(event.target.value))}>
            <option value={1}>1h</option>
            <option value={2}>2h</option>
            <option value={4}>4h</option>
            <option value={8}>8h</option>
          </select>
        </label>

        <label className="patternField">
          <span>Max event gap</span>
          <select value={maxGapHours} onChange={(event) => setMaxGapHours(Number(event.target.value))}>
            <option value={4}>4h</option>
            <option value={12}>12h</option>
            <option value={24}>24h</option>
            <option value={72}>3d</option>
          </select>
        </label>

        <label className="patternField patternSmallField">
          <span>Min scored</span>
          <input
            type="number"
            min="1"
            max="50"
            value={minSamples}
            onChange={(event) => setMinSamples(Number(event.target.value))}
          />
        </label>

        <label className="patternField patternSmallField">
          <span>Patterns</span>
          <input
            type="number"
            min="1"
            max="40"
            value={maxPatterns}
            onChange={(event) => setMaxPatterns(Number(event.target.value))}
          />
        </label>

        {clientSideAnalysis && (
          <label className="patternField patternSmallField">
            <span>Score per pattern</span>
            <input
              type="number"
              min="4"
              max="40"
              value={maxExamplesPerPattern}
              onChange={(event) => setMaxExamplesPerPattern(Number(event.target.value))}
            />
          </label>
        )}

        <label className="patternField patternSmallField">
          <span>Min win %</span>
          <input
            type="number"
            min="0"
            max="100"
            value={minWinRate}
            onChange={(event) => setMinWinRate(Number(event.target.value))}
          />
        </label>

        <label className="patternField patternSmallField">
          <span>Min close %</span>
          <input
            type="number"
            step="0.1"
            value={minAvgClose}
            onChange={(event) => setMinAvgClose(Number(event.target.value))}
          />
        </label>

        <label className="patternField patternSmallField">
          <span>Min fav/pain</span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={minFavPain}
            onChange={(event) => setMinFavPain(Number(event.target.value))}
          />
        </label>

        <label className="patternField">
          <span>Sort by</span>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            <option value="avgCloseReturn">Avg close</option>
            <option value="winRate">Win rate</option>
            <option value="expansionToPain">Fav/pain</option>
            <option value="avgFavorable">Avg favorable</option>
            <option value="samples">Scored samples</option>
            <option value="seenOccurrences">Seen occurrences</option>
          </select>
        </label>

        <label className="patternField">
          <span>Timeframe mode</span>
          <select value={timeframeMode} onChange={(event) => setTimeframeMode(event.target.value)}>
            <option value="any">Any</option>
            <option value="same">Same only</option>
            <option value="cross">Cross only</option>
          </select>
        </label>

        {clientSideAnalysis && (
          <button
            className="patternAnalyzeButton"
            type="button"
            disabled={isAnalyzing || !candidates.length}
            onClick={runAnalysis}
          >
            {isAnalyzing ? "Analyzing..." : "Analyze"}
          </button>
        )}
      </div>

      <div className="patternStats">
        <span>{clientSideAnalysis ? flatEvents.length : serverData?.events || 0} events</span>
        <span>{clientSideAnalysis ? candidates.length : serverData?.candidateSequences || 0} candidate sequences</span>
        <span>{visibleResults.length} shown</span>
        {!clientSideAnalysis && serverData?.samplesChecked ? <span>{serverData.samplesChecked} samples checked</span> : null}
        {!clientSideAnalysis && serverData?.durationMs ? <span>{(serverData.durationMs / 1000).toFixed(1)}s server run</span> : null}
        {progressText ? <span>{progressText}</span> : null}
        {!clientSideAnalysis && serverError ? <span className="patternErrorText">Server research unavailable</span> : null}
        {!clientSideAnalysis && !serverError && !serverData ? <span>Waiting for server run</span> : null}
        {analysisError ? <span className="patternErrorText">{analysisError}</span> : null}
      </div>

      {!activeResults.length && !isAnalyzing && (
        <div className="patternEmpty">
          {clientSideAnalysis
            ? "Run analysis to score event sequences against Binance futures candles."
            : serverError
              ? "Server-side pattern research is unavailable. You can switch on client-side analysis meanwhile."
              : "Server-side pattern research has not produced data yet."}
        </div>
      )}

      {visibleResults.length > 0 && (
        <div className="patternResults">
          {visibleResults.map((pattern) => (
            <PatternCard
              key={pattern.key}
              pattern={pattern}
              openPopup={openPopup}
              flatEvents={flatEvents}
            />
          ))}
        </div>
      )}

      {activeResults.length > 0 && !visibleResults.length && (
        <div className="patternEmpty">
          No scored pattern reached the current sample filter.
        </div>
      )}
    </div>
  );
}

function ResearchTop({ onRefresh, isValidating, clientSideAnalysis }) {
  return (
    <div className="patternTop">
      <div>
        <div className="patternTitle">Pattern Research</div>
        <div className="patternSub">
          {clientSideAnalysis ? "Client-side sequence mining" : "Server-side sequence mining"} {isValidating ? "updating..." : ""}
        </div>
      </div>
      <button className="patternRefreshButton" type="button" onClick={onRefresh}>Refresh</button>
    </div>
  );
}

function PatternCard({ pattern, openPopup, flatEvents }) {
  const [open, setOpen] = useState(false);
  const colorClass = pattern.direction === "long" ? "patternLong" : "patternShort";
  const bestExamples = pattern.examples.slice(0, 6);

  return (
    <div className={`patternCard ${colorClass}`}>
      <button className="patternCardHead" type="button" onClick={() => setOpen((value) => !value)}>
        <span className="patternDirection">{pattern.direction.toUpperCase()}</span>
        <span className="patternLabel">{pattern.label}</span>
        <span className="patternChevron">{open ? "v" : ">"}</span>
      </button>

      <div className="patternMetrics">
        <Metric label="Seen" value={pattern.seenOccurrences} />
        <Metric label="Seen coins" value={pattern.seenCoinCount} />
        <Metric label="Scored" value={pattern.samples} />
        <Metric label="Scored coins" value={pattern.scoredCoinCount} />
        {pattern.horizonHours ? <Metric label="Window" value={`${pattern.horizonHours}h`} /> : null}
        {pattern.maxGapHours ? <Metric label="Gap" value={`${pattern.maxGapHours}h`} /> : null}
        {pattern.timeframeMode ? <Metric label="TF" value={formatTimeframeMode(pattern.timeframeMode)} /> : null}
        <Metric label="Win" value={formatPercent(pattern.winRate * 100)} strong={pattern.winRate >= 0.6} />
        <Metric label="Avg fav" value={formatPercent(pattern.avgFavorable)} />
        <Metric label="Med pain" value={formatPercent(pattern.medianAdverse)} />
        <Metric label="Avg close" value={formatPercent(pattern.avgCloseReturn)} strong={pattern.avgCloseReturn > 0} />
        <Metric
          label="Fav/pain"
          value={pattern.expansionToPain == null ? "-" : pattern.expansionToPain.toFixed(2)}
        />
      </div>

      {open && (
        <div className="patternExamples">
          {bestExamples.map((occurrence) => (
            <button
              key={`${occurrence.cur}-${occurrence.second.ts}-${occurrence.first.ts}`}
              className="patternExample"
              type="button"
              onClick={() => openPatternExample(openPopup, pattern, occurrence, flatEvents)}
            >
              <span className="patternExampleCur">
                <AssetLabel
                  symbol={occurrence.cur}
                  event={occurrence.second || occurrence.first}
                  className="assetLabelCompact"
                />
              </span>
              <span>{formatDate(occurrence.second.ts)}</span>
              <span>gap {formatGap(occurrence.gapSec)}</span>
              <span className={occurrence.outcome.closeReturn >= 0 ? "patternPositive" : "patternNegative"}>
                close {formatPercent(occurrence.outcome.closeReturn)}
              </span>
              <span>fav {formatPercent(occurrence.outcome.favorable)}</span>
              <span>pain {formatPercent(occurrence.outcome.adverse)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, strong }) {
  return (
    <div className={strong ? "patternMetric patternMetricStrong" : "patternMetric"}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "-";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(2)}%`;
}

function formatDate(ts) {
  if (!ts) return "";
  return new Date(ts * 1000).toLocaleString([], {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatGap(sec) {
  if (!sec) return "0m";
  const minutes = Math.round(sec / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function formatTimeframeMode(value) {
  if (value === "same") return "Same";
  if (value === "cross") return "Cross";
  return "Any";
}

function comparePatternResults(a, b, sortBy) {
  const value = (result) => {
    if (sortBy === "winRate") return result.winRate || 0;
    if (sortBy === "expansionToPain") return result.expansionToPain || 0;
    if (sortBy === "avgFavorable") return result.avgFavorable || 0;
    if (sortBy === "samples") return result.samples || 0;
    if (sortBy === "seenOccurrences") return result.seenOccurrences || 0;
    return result.avgCloseReturn || 0;
  };

  const diff = value(b) - value(a);
  if (diff !== 0) return diff;
  if ((b.winRate || 0) !== (a.winRate || 0)) return (b.winRate || 0) - (a.winRate || 0);
  return (b.samples || 0) - (a.samples || 0);
}
